/**
 * Checks if a user is a staff by verifying that both the token and businessSlug are not null or empty.
 * @param {string | null} token - The token of the business that the user is associated with
 * @param {string | null} businessSlug - The slug of the business that the user is associated with
 * @returns {boolean} - True if the user is a staff, false otherwise
 */
export const checkIsStaff = (
  token: string | null,
  businessSlug: string | null
) =>
  token !== null &&
  token !== "" &&
  businessSlug !== null &&
  businessSlug !== "";

/**
 * Checks if a user is a client by verifying that the businessSlug is not null or empty
 * and that the token is null or empty.
 * @param {string | null} businessSlug - The slug of the business that the user is associated with
 * @param {string | null} token - The token of the business that the user is associated with
 * @returns {boolean} - True if the user is a client, false otherwise
 */
export const checkIsClient = (
  businessSlug: string | null,
  token: string | null
) =>
  businessSlug !== null &&
  businessSlug !== "" &&
  (token === null || token === "");

/**
 * Returns the user type based on if the user is a staff or client.
 * @param {boolean} isStaff - True if the user is a staff, false otherwise
 * @param {boolean} isClient - True if the user is a client, false otherwise
 * @param {{Owner: string, Staff: string[], Client: string}} roles - The roles object with the user types
 * @returns {string | string[]} - The user type, either a single role or an array of roles
 */
export const getUserType = (
  isStaff: boolean,
  isClient: boolean,
  roles: any
) => {
  let userType: string | string[];

  if (isStaff) {
    userType = roles.Staff[0];
  } else if (isClient) {
    userType = roles.Client;
  } else {
    userType = roles.Owner;
  }

  return userType;
};

/**
 * Returns a message to the user based on whether they are a staff member or not.
 * If they are a staff member, it will try to use the business name if available,
 * falling back to a catch-all if it is not. If they are not a staff member,
 * it will return a generic message.
 * @param {boolean} isStaff - True if the user is a staff member, false otherwise
 * @param {string | null} businessName - The name of the business the user is associated with
 * @returns {string} - The message to display to the user
 */
export const getTeamMessage = (
  isStaff: boolean,
  businessName: string | null
) => {
  let teamMessage = "";
  if (isStaff) {
    if (businessName) {
      teamMessage = `Welcome to the ${businessName} team! Tell us about yourself and get started.`;
    } else {
      teamMessage =
        "Welcome to the team! Tell us about yourself and get started.";
    }
  } else {
    teamMessage = "Tell us about yourself and get started!";
  }
  return teamMessage;
};

/**
 * Returns a message to the user based on whether they are a client, staff member, or neither.
 * If they are a client, it will return the client message. If they are a staff member,
 * it will return the team message. If they are neither, it will return a generic message.
 * @param {boolean} isClient - True if the user is a client, false otherwise
 * @param {string} clientMessage - The message to return if the user is a client
 * @param {boolean} isStaff - True if the user is a staff member, false otherwise
 * @param {string} teamMessage - The message to return if the user is a staff member
 * @returns {string} - The message to display to the user
 */
export function getInfoText(
  isClient: boolean,
  clientMessage: string,
  isStaff: boolean,
  teamMessage: string
): string {
  if (isClient) {
    return clientMessage;
  }

  if (isStaff) {
    return teamMessage;
  }

  return "The smarter way to handle taxes with groundbreaking innovation!";
}

/**
 * Returns a message to the user based on whether they are a client or not.
 * If they are a client, it will try to use the first and last name from personalInfo,
 * falling back to the business name if available, and finally a catch-all if neither
 * is available. If they are not a client, it will return a generic message.
 * @param {boolean} isClient - True if the user is a client, false otherwise
 * @param {BusinessContent} businessContent - The content of the business the user is associated with
 * @param {string} [taxProName] - Optional name of the tax professional
 * @returns {string} - The message to display to the user
 */
export const getClientMessage = (
  isClient: boolean,
  businessContent: {
    businessName?: string;
    businessOwner?: string;
    loading?: boolean;
  },
  taxProName?: string
): string => {
  let clientMessage = "";
  // only do the personalized logic if isClient and not loading
  if (isClient && !businessContent?.loading) {
    // 1) If we have either first or last name from personalInfo, use that
    if (taxProName && taxProName !== "") {
      clientMessage = `${taxProName} has invited you to create an account that will help them with filing your taxes`;
    }
    // 2) Otherwise fall back to business name if available
    else if (businessContent?.businessName) {
      clientMessage = `${businessContent?.businessOwner} with ${businessContent?.businessName} has invited you to create an account that will help them with filing your taxes`;
    }
    // 3) Finally, if we have no personal names and no business name, default to catch-all
    else {
      clientMessage = `${
        businessContent?.businessOwner ?? "Your tax pro"
      } has invited you to create an account that will help them with filing your taxes`;
    }
  } else {
    clientMessage = "Fill out the information below to create your account";
  }

  return clientMessage;
};

/**
 * Determines the user type based on the presence of token and businessSlug
 * @param {string | null} businessSlug - The slug of the business that the user is associated with
 * @param {string | null} token - The token of the business that the user is associated with
 * @param {{Owner: string, Staff: string[], Client: string}} roles - The roles object with the user types
 * @returns {string} - The user type based on the parameters
 */
export const determineUserType = (
  businessSlug: string | null,
  token: string | null,
  roles: any
): string => {
  if (token && businessSlug) {
    return roles.Staff[0]; // team_member
  } else if (businessSlug) {
    return roles.Client;
  } else {
    return roles.Owner;
  }
};

/**
 * Generates appropriate welcome message based on user type and available information
 * @param {string} userType - The type of user (team_member, client, or owner)
 * @param {Object} options - Additional information for message generation
 * @param {string} [options.businessName] - Name of the business
 * @param {string} [options.taxProName] - Name of the tax professional
 * @param {string} [options.businessOwner] - Name of the business owner
 * @returns {string} - The appropriate welcome message
 */
export const generateWelcomeMessage = (
  userType: string,
  options: {
    businessName?: string;
    taxProName?: string;
    businessOwner?: string;
  } = {}
): string => {
  const { businessName, taxProName, businessOwner } = options;

  switch (userType) {
    case "team_member":
      return businessName
        ? `Welcome to the ${businessName} team! Tell us about yourself and get started.`
        : "Welcome to the team! Tell us about yourself and get started.";

    case "client":
      if (taxProName) {
        return `${taxProName} has invited you to create an account that will help them with filing your taxes`;
      } else if (businessName && businessOwner) {
        return `${businessOwner} with ${businessName} has invited you to create an account that will help them with filing your taxes`;
      } else {
        return `${businessOwner ?? "Your tax pro"} has invited you to create an account that will help them with filing your taxes`;
      }

    default: // owner
      return "Start here by creating your <strong>Tax Pro</strong> account.";
  }
};
